import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { 
  getHelpNavigation, getHelpTopicDetail, searchHelpFaq, 
  adminListCategories, adminCreateCategory, adminUpdateCategory, adminDeleteCategory,
  adminListTopics, adminCreateTopic, adminUpdateTopic, adminDeleteTopic,
  adminListFaqs, adminCreateFaq, adminUpdateFaq, adminDeleteFaq, adminPublishFaq,
  Category, Faq, TopicResponse, SearchItem 
} from '../composables/fetchAPI';
import ReportIssueButton from '../components/ReportIssueButton';
import { toast } from 'react-hot-toast';

function SearchIcon() {
  return (
    <svg aria-hidden="true" className="h-5 w-5 text-[var(--secondary-text-color)]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <circle cx="11" cy="11" r="7" /><path d="m20 20-4-4" />
    </svg>
  );
}

interface FaqPageProps {
  userInfo?: any;
}

export default function FaqPage({ userInfo }: FaqPageProps) {
  // ================= USER STATES =================
  const [categories, setCategories] = useState<Category[]>([]);
  const [categorySlug, setCategorySlug] = useState('');
  const [topicSlug, setTopicSlug] = useState('');
  const [topic, setTopic] = useState<TopicResponse['topic'] | null>(null);
  const [faqs, setFaqs] = useState<Faq[]>([]);
  const [searchResults, setSearchResults] = useState<SearchItem[]>([]);
  const [query, setQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // ================= ADMIN STATES =================
  const isAdmin = userInfo?.isAdmin || false;
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [adminTab, setAdminTab] = useState<'category' | 'topic' | 'faq'>('category');
  const [adminViewMode, setAdminViewMode] = useState<'list' | 'form'>('list');
  const [adminDataList, setAdminDataList] = useState<any[]>([]);
  const [isFetchingAdmin, setIsFetchingAdmin] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [editingItem, setEditingItem] = useState<any>(null);

  // Admin Form States
  const [formCategory, setFormCategory] = useState({ name: '', slug: '', description: '', order: 0, status: 'published' });
  const [formTopic, setFormTopic] = useState({ categoryId: '', name: '', slug: '', descriptionHtml: '', tags: '', order: 0, status: 'published' });
  const [formFaq, setFormFaq] = useState({ categoryId: '', topicId: '', question: '', answerHtml: '', tags: '', keywords: '', order: 0, status: 'published' });

  const searchRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  // ================= USER LOGIC =================
  const activeCategory = useMemo(
    () => categories.find((category) => category.slug === categorySlug),
    [categories, categorySlug]
  );

  const updateUrl = useCallback((category: string, selectedTopic: string, faqId = '') => {
    const params = new URLSearchParams();
    if (category) params.set('cat', category);
    if (selectedTopic) params.set('topic', selectedTopic);
    if (faqId) params.set('faq', faqId);
    window.history.pushState({}, '', `${window.location.pathname}?${params.toString()}`);
  }, []);

  const loadTopic = useCallback(async (category: string, selectedTopic: string, faqId = '') => {
    if (!category || !selectedTopic) return;
    setLoading(true); setError(''); setQuery(''); setIsSearching(false); setIsMobileMenuOpen(false);
    setCategorySlug(category); setTopicSlug(selectedTopic); updateUrl(category, selectedTopic, faqId);

    try {
      const data = await getHelpTopicDetail(category, selectedTopic);
      setTopic(data.topic);
      setFaqs(data.faqs);
      if (faqId) window.setTimeout(() => document.getElementById(`faq-${faqId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 0);
    } catch (cause: any) {
      setError(cause instanceof Error ? cause.message : (cause?.th_message || 'เกิดข้อผิดพลาด'));
    } finally {
      setLoading(false);
    }
  }, [updateUrl]);

  const selectCategory = (category: Category) => {
    if (category.topics && category.topics.length > 0) {
      loadTopic(category.slug, category.topics[0].slug);
    }
  };

  const fetchNavigation = useCallback(async () => {
    try {
      const data = await getHelpNavigation();
      setCategories(data);
      return data;
    } catch (cause: any) {
      setError(cause instanceof Error ? cause.message : (cause?.th_message || 'เกิดข้อผิดพลาด'));
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNavigation().then((data) => {
      if (data && data.length > 0) {
        const params = new URLSearchParams(window.location.search);
        const initialCategory = params.get('cat') || data[0]?.slug || '';
        const selectedCategory = data.find((item) => item.slug === initialCategory) || data[0];
        loadTopic(selectedCategory?.slug || '', params.get('topic') || selectedCategory?.topics[0]?.slug || '', params.get('faq') || '');
      }
    });
  }, [fetchNavigation, loadTopic]);

  useEffect(() => {
    if (!query.trim()) { setSearchResults([]); setIsSearching(false); return; }
    const timer = window.setTimeout(async () => {
      try {
        const data = await searchHelpFaq(query);
        setSearchResults(data.items || []); setIsSearching(true);
      } catch (err) { console.error(err); }
    }, 300);
    return () => window.clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) setIsSearching(false);
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node)) setIsMobileMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectSuggestion = (item: SearchItem) => {
    setQuery(''); setIsSearching(false);
    if ((item as any).category && (item as any).topic) {
      loadTopic((item as any).category.slug, (item as any).topic.slug, item.id);
    } else {
      window.setTimeout(() => document.getElementById(`faq-${item.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
    }
  };

  const clearSearch = () => {
    setQuery('');
    setSearchResults([]);
    setIsSearching(false);
  };

  // ================= ADMIN LOGIC =================
  const loadAdminDataList = useCallback(async () => {
    setIsFetchingAdmin(true);
    try {
      let result: any;
      if (adminTab === 'category') result = await adminListCategories();
      else if (adminTab === 'topic') result = await adminListTopics();
      else if (adminTab === 'faq') result = await adminListFaqs();
      
      const list = result?.data || result?.items || (Array.isArray(result) ? result : []);
      setAdminDataList(list);
    } catch (err: any) {
      toast.error(err.message || 'โหลดข้อมูล Admin ไม่สำเร็จ');
    } finally {
      setIsFetchingAdmin(false);
    }
  }, [adminTab]);

  useEffect(() => {
    if (showAdminModal && adminViewMode === 'list') {
      loadAdminDataList();
    }
  }, [adminTab, showAdminModal, adminViewMode, loadAdminDataList]);

  const openMasterAdminModal = () => {
    setAdminTab('category');
    setAdminViewMode('list');
    setShowAdminModal(true);
  };

  const handleOpenCreateForm = () => {
    setModalMode('create');
    if (adminTab === 'category') setFormCategory({ name: '', slug: '', description: '', order: 0, status: 'published' });
    if (adminTab === 'topic') setFormTopic({ categoryId: '', name: '', slug: '', descriptionHtml: '', tags: '', order: 0, status: 'published' });
    if (adminTab === 'faq') setFormFaq({ categoryId: '', topicId: '', question: '', answerHtml: '', tags: '', keywords: '', order: 0, status: 'published' });
    setAdminViewMode('form');
  };

  const handleOpenEditForm = (item: any) => {
    setModalMode('edit');
    setEditingItem(item);
    if (adminTab === 'category') setFormCategory({ name: item.name, slug: item.slug, description: item.description || '', order: item.order || 0, status: item.status || 'published' });
    if (adminTab === 'topic') setFormTopic({ categoryId: item.categoryId || '', name: item.name, slug: item.slug, descriptionHtml: item.descriptionHtml || '', tags: item.tags?.join(', ') || '', order: item.order || 0, status: item.status || 'published' });
    if (adminTab === 'faq') setFormFaq({ categoryId: item.categoryId || '', topicId: item.topicId || '', question: item.question, answerHtml: item.answerHtml || '', tags: item.tags?.join(', ') || '', keywords: item.keywords?.join(', ') || '', order: item.order || 0, status: item.status || 'published' });
    setAdminViewMode('form');
  };

  const handleAdminDelete = async (id: string) => {
    if (!window.confirm('🚨 ยืนยันการลบข้อมูลนี้?')) return;
    try {
      if (adminTab === 'category') await adminDeleteCategory(id);
      if (adminTab === 'topic') await adminDeleteTopic(id);
      if (adminTab === 'faq') await adminDeleteFaq(id);
      toast.success('ลบข้อมูลสำเร็จ');
      loadAdminDataList();
      fetchNavigation(); 
      if (topicSlug) loadTopic(categorySlug, topicSlug);
    } catch (err: any) { toast.error(err.message || 'เกิดข้อผิดพลาด'); }
  };

  const handleAdminTogglePublish = async (id: string, currentStatus: string) => {
    const newStatus = currentStatus === 'published' ? 'draft' : 'published';
    try {
      if (adminTab === 'faq') {
        await adminPublishFaq(id, { status: newStatus });
        toast.success(`เปลี่ยนสถานะเป็น ${newStatus}`);
        loadAdminDataList();
        if (topicSlug) loadTopic(categorySlug, topicSlug);
      }
    } catch (err: any) { toast.error(err.message || 'เกิดข้อผิดพลาด'); }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (adminTab === 'category') {
        if (modalMode === 'create') await adminCreateCategory(formCategory);
        else await adminUpdateCategory(editingItem.id, { name: formCategory.name, description: formCategory.description, order: formCategory.order });
      } 
      else if (adminTab === 'topic') {
        const payload = { ...formTopic, tags: formTopic.tags.split(',').map(t => t.trim()).filter(Boolean) };
        if (modalMode === 'create') await adminCreateTopic(payload);
        else await adminUpdateTopic(editingItem.id, { name: formTopic.name, descriptionHtml: formTopic.descriptionHtml, tags: payload.tags, order: formTopic.order });
      } 
      else if (adminTab === 'faq') {
        const payload = { ...formFaq, tags: formFaq.tags.split(',').map(t => t.trim()).filter(Boolean), keywords: formFaq.keywords.split(',').map(k => k.trim()).filter(Boolean) };
        if (modalMode === 'create') await adminCreateFaq(payload);
        else await adminUpdateFaq(editingItem.id, { question: formFaq.question, answerHtml: formFaq.answerHtml, tags: payload.tags, keywords: payload.keywords, order: formFaq.order });
      }
      
      toast.success('บันทึกข้อมูลสำเร็จ');
      setAdminViewMode('list');
      loadAdminDataList();
      fetchNavigation();
      if (topicSlug) loadTopic(categorySlug, topicSlug);
    } catch (err: any) { toast.error(err.message || 'เกิดข้อผิดพลาด'); }
  };

  return (
    <div className="min-h-screen transition-colors duration-200 pb-16 bg-[var(--component-bg-color)] text-[var(--text-color)]">
      
      {/* 🌟 HERO HEADER */}
      <header className="relative px-5 py-14 sm:px-8 border-b text-center shadow-md rounded-b-[40px] z-20 bg-[var(--bg-chart)] border-[var(--border-item-light)]">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 via-yellow-500/5 to-transparent pointer-events-none rounded-b-[40px]"></div>
        <div className="mx-auto max-w-4xl relative z-30 flex flex-col items-center">
          
          <span className="text-xs font-black tracking-widest px-4 py-1.5 rounded-full mb-4 shadow-md uppercase border bg-[var(--bg-item)] border-[var(--border-item-light)] text-[var(--yellow-text-color)]">
            💬 Help Center & Support
          </span>
          <h1 className="text-3xl sm:text-5xl font-black tracking-tight text-[var(--yellow-text-color)]">มีอะไรให้เราช่วยเหลือไหม?</h1>
          <p className="mt-2 text-sm sm:text-base font-medium opacity-80 text-[var(--secondary-text-color)]">ค้นหาคำตอบ คู่มือ และทุกเรื่องที่คุณสงสัยเกี่ยวกับ WeThaiInvest</p>

          {/* 🛡️ ADMIN MAIN BUTTON */}
          {isAdmin && (
            <div className="mt-5">
              <button onClick={openMasterAdminModal} className="px-6 py-2.5 rounded-full text-xs font-black bg-[var(--button-edit-bg)] text-[var(--text-black)] shadow-[0_4px_15px_rgba(250,204,21,0.3)] hover:scale-105 transition-transform">
                🛠️ จัดการข้อมูลศูนย์ช่วยเหลือ (Admin CMS)
              </button>
            </div>
          )}
          
          <div className="relative mt-6 w-full max-w-2xl" ref={searchRef}>
            <div className="relative">
              <span className="absolute inset-y-0 left-5 flex items-center pointer-events-none"><SearchIcon /></span>
              <input 
                value={query} onChange={(e) => setQuery(e.target.value)} onFocus={() => { if (query.trim()) setIsSearching(true); }}
                placeholder="ค้นหา เช่น โหมดพอร์ต, CSV, เงินปันผล..." 
                className="w-full rounded-full py-4 pl-14 pr-14 text-sm shadow-[0_8px_20px_rgba(0,0,0,0.15)] outline-none border-2 transition-all relative z-40 bg-[var(--bg-item)] text-[var(--text-color)] border-[var(--border-item-light)] focus:border-[var(--yellow-text-color)]"
              />
              {query && <button onClick={() => {setQuery(''); setSearchResults([]); setIsSearching(false)}} className="absolute right-5 top-4 rounded-full w-7 h-7 flex items-center justify-center font-bold text-xs shadow-sm bg-[var(--hover-item)] transition z-50 text-[var(--text-color)]">✕</button>}
            </div>

            {isSearching && searchResults.length > 0 && (
              <div className="absolute left-0 right-0 mt-3 max-h-96 overflow-y-auto rounded-3xl border-2 shadow-[0_20px_50px_rgba(0,0,0,0.5)] z-50 p-3 space-y-2 text-left backdrop-blur-xl bg-[var(--bg-item)] border-[var(--border-item-light)]">
                <div className="px-4 py-2 text-[11px] font-bold tracking-wider uppercase opacity-60 border-b border-[var(--border-item-light)] text-[var(--yellow-text-color)]">
                  ผลการค้นหาแนะนำ ({searchResults.length})
                </div>
                {searchResults.map((item: any) => (
                  <div key={item.id} onClick={() => handleSelectSuggestion(item)} className="group flex items-center gap-3 p-3.5 rounded-2xl cursor-pointer transition-all hover:translate-x-1 border border-transparent bg-[var(--component-bg-color)]">
                    <span className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-black shrink-0 border shadow-sm bg-[var(--bg-item)] border-[var(--border-item-light)] text-[var(--yellow-text-color)]">?</span>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-xs sm:text-sm font-extrabold truncate group-hover:text-[var(--yellow-text-color)] transition-colors text-[var(--text-color)]">{item.question}</h4>
                      {item.category && item.topic && <p className="text-[10px] opacity-70 truncate mt-0.5 text-[var(--secondary-text-color)]">{item.category.name} › {item.topic.name}</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="mt-5 flex flex-wrap justify-center gap-2 text-xs">
            <span className="opacity-60 self-center font-semibold text-[var(--text-color)]">คำค้นยอดฮิต:</span>
            {['โหมดพอร์ต', 'นำเข้า CSV', 'เงินปันผล', 'Cost Basis'].map((tag) => (
              <button key={tag} onClick={() => setQuery(tag)} className="px-4 py-1.5 rounded-full border transition hover:scale-105 active:scale-95 font-bold shadow-[0_4px_10px_rgba(0,0,0,0.1)] bg-[var(--bg-item)] border-[var(--border-item-light)] text-[var(--text-color)]">#{tag}</button>
            ))}
          </div>
        </div>
      </header>

      {/* 🌟 MAIN CONTENT LAYOUT */}
      <div className="mx-auto max-w-7xl px-5 py-8 sm:px-8">
        <div className="relative mb-6 block md:hidden" ref={mobileMenuRef}>
          <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="w-full rounded-2xl p-4 flex items-center justify-between shadow-[0_4px_15px_rgba(0,0,0,0.15)] border font-bold text-xs bg-[var(--bg-item)] border-[var(--border-item-light)] text-[var(--text-color)]">
            <span className="flex items-center gap-3"><span className="text-lg">☰</span><span className="text-sm font-black">เลือกหัวข้อ</span></span>
            <span className="text-[11px] opacity-70 truncate max-w-[150px] text-[var(--yellow-text-color)]">{activeCategory?.name} {topic ? `• ${topic.name}` : ''}</span>
          </button>
          {isMobileMenuOpen && (
            <div className="absolute left-0 right-0 mt-2 rounded-3xl border-2 shadow-[0_15px_35px_rgba(0,0,0,0.4)] z-40 p-4 space-y-4 max-h-[70vh] overflow-y-auto bg-[var(--bg-item)] border-[var(--border-item-light)]">
              {categories.map((cat) => (
                <div key={cat.id} className="space-y-1.5">
                  <div className="text-xs font-black px-2 py-1 uppercase tracking-wider text-[var(--yellow-text-color)]">{cat.name}</div>
                  <div className="space-y-1 pl-2">
                    {cat.topics?.map((item) => (
                      <button key={item.id} onClick={() => loadTopic(cat.slug, item.slug)} className="w-full rounded-xl px-3 py-2.5 text-left text-xs font-bold transition flex items-center justify-between" style={{ backgroundColor: item.slug === topicSlug ? 'var(--button-edit-bg)' : 'var(--component-bg-color)', color: item.slug === topicSlug ? 'var(--text-black)' : 'var(--text-color)' }}>
                        <span>{item.name}</span><span className="text-[10px] opacity-60">›</span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="grid gap-8 md:grid-cols-[21rem_1fr] items-start">
          <aside className="hidden md:block rounded-3xl border p-5 shadow-[0_12px_30px_rgba(0,0,0,0.2)] space-y-6 bg-[var(--bg-item)] border-[var(--border-item-light)]">
            <p className="px-2 text-xs font-black uppercase tracking-wider text-[var(--yellow-text-color)]">📁 หมวดหมู่ช่วยเหลือ</p>
            <nav className="space-y-4">
              {categories.map((category) => {
                const isCategoryActive = category.slug === categorySlug;
                return (
                  <div key={category.id} className="space-y-2">
                    <div onClick={() => selectCategory(category)} className="cursor-pointer rounded-2xl px-4 py-3 text-xs font-black transition-all flex items-center justify-between shadow-[0_4px_12px_rgba(0,0,0,0.1)] border" style={{ backgroundColor: isCategoryActive ? 'var(--button-edit-bg)' : 'var(--component-bg-color)', color: isCategoryActive ? 'var(--text-black)' : 'var(--text-color)', borderColor: 'var(--border-item-light)' }}>
                      <span className="flex items-center gap-2.5"><span className="w-2.5 h-2.5 rounded-full shadow-inner" style={{ backgroundColor: isCategoryActive ? 'var(--text-black)' : 'var(--yellow-text-color)' }}></span>{category.name}</span>
                    </div>
                    {category.topics && category.topics.length > 0 && (
                      <div className="pl-4 space-y-1.5 pt-1 border-l-2 ml-4 border-[var(--yellow-text-color)]">
                        {category.topics.map((item) => {
                          const isTopicActive = item.slug === topicSlug;
                          return (
                            <button key={item.id} onClick={() => loadTopic(category.slug, item.slug)} className={`w-full rounded-xl px-3 py-2 text-left text-xs font-bold transition-all shadow-[0_2px_8px_rgba(0,0,0,0.08)] ${isTopicActive ? 'scale-[1.02] translate-x-1' : 'opacity-75 hover:opacity-100'}`} style={{ backgroundColor: isTopicActive ? 'var(--yellow-text-color)' : 'var(--component-bg-color)', color: isTopicActive ? 'var(--text-black)' : 'var(--text-color)', border: isTopicActive ? '1px solid var(--border-item-light)' : 'none' }}>
                              • {item.name}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>
            <div className="p-5 rounded-3xl border text-center shadow-[0_8px_20px_rgba(0,0,0,0.15)] mt-6 bg-[var(--component-bg-color)] border-[var(--border-item-light)]">
              <div className="text-2xl mb-1">💡</div>
              <h4 className="text-xs font-black mb-1 text-[var(--yellow-text-color)]">ยังไม่เจอคำตอบใช่ไหม?</h4>
              <p className="text-[11px] mb-4 opacity-80 text-[var(--secondary-text-color)]">ทีมงานพร้อมซัพพอร์ตคุณเสมอ</p>
              <ReportIssueButton jobId="FAQ_SUPPORT" errorMessage="ติดต่อสอบถามผ่านศูนย์ช่วยเหลือ (FAQ)" />
            </div>
          </aside>

          <main className="min-w-0 space-y-6">
            <div className="bg-transparent pb-2">
              <p className="text-xs font-medium text-[var(--secondary-text-color)]">ศูนย์ช่วยเหลือ <span className="font-bold text-[var(--yellow-text-color)]">/ {query ? 'ผลการค้นหา' : `${activeCategory?.name} › ${topic?.name}`}</span></p>
              {!query && topic && (
                <div className="mt-4 p-5 rounded-3xl border shadow-lg flex items-center gap-4 bg-[var(--bg-item)] border-[var(--border-item-light)]">
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-xl shadow-md font-black bg-[var(--button-edit-bg)] text-[var(--text-black)]">↗</div>
                  <div>
                    <p className="text-[11px] font-bold opacity-70 uppercase tracking-wider text-[var(--secondary-text-color)]">{activeCategory?.name}</p>
                    <h2 className="text-lg sm:text-2xl font-black tracking-tight text-[var(--text-color)]">{topic.name}</h2>
                    <span className="text-[11px] font-semibold opacity-60 mt-0.5 block">{faqs.length} คำถามที่พบบ่อย</span>
                  </div>
                </div>
              )}
            </div>

            {topic?.descriptionHtml && <div className="text-sm p-5 rounded-3xl border shadow-[0_8px_20px_rgba(0,0,0,0.15)] leading-relaxed bg-[var(--bg-item)] border-[var(--border-item-light)] text-[var(--secondary-text-color)]" dangerouslySetInnerHTML={{ __html: topic.descriptionHtml }} />}

            <section className="space-y-4" aria-live="polite">
              <div className="text-xs font-black uppercase tracking-wider pt-2 opacity-80 text-[var(--yellow-text-color)]">คำถามที่พบบ่อย</div>
              {loading && [1, 2, 3].map((item) => <div key={item} className="h-20 animate-pulse rounded-3xl shadow-md bg-[var(--bg-item)]" />)}
              {!loading && error && <div className="rounded-3xl p-5 text-sm border shadow-md bg-[var(--theme-dark-red-bg)] border-[var(--theme-dark-red-border)] text-[var(--red-text-color)]">{error}</div>}
              {!loading && !error && faqs.length === 0 && (
                <div className="py-16 text-center rounded-3xl border-2 border-dashed shadow-inner border-[var(--border-item-light)]">
                  <p className="text-sm font-bold text-[var(--secondary-text-color)]">ไม่พบคำถาม</p>
                </div>
              )}
              {!loading && faqs.map((faq, index) => (
                <details key={faq.id} id={`faq-${faq.id}`} className="group rounded-3xl border-2 shadow-[0_8px_25px_rgba(0,0,0,0.15)] transition-all overflow-hidden bg-[var(--bg-item)] border-[var(--border-item-light)]">
                  <summary className="flex cursor-pointer list-none items-center gap-4 px-6 py-5 font-bold text-sm marker:hidden transition hover:opacity-90 text-[var(--text-color)]">
                    <span className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-black shrink-0 border shadow-sm bg-[var(--component-bg-color)] border-[var(--border-item-light)] text-[var(--yellow-text-color)]">{String(index + 1).padStart(2, '0')}</span>
                    <span className="flex-1 font-extrabold">{faq.question}</span>
                    <span className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border shadow-sm transition-transform group-open:rotate-180 bg-[var(--component-bg-color)] border-[var(--border-item-light)] text-[var(--yellow-text-color)]">▼</span>
                  </summary>
                  <div className="px-7 pb-6 text-xs sm:text-sm leading-relaxed border-t pt-5 space-y-4 rounded-b-3xl border-[var(--border-item-light)] bg-[var(--component-bg-color)]">
                    <div className="prose max-w-none font-normal whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: faq.answerHtml }} />
                    {faq.tags?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 pt-3 border-t border-gray-500/10">
                        {faq.tags.map((tag: string) => <span key={tag} className="rounded-full px-3 py-0.5 text-[10px] font-black border shadow-sm bg-[var(--button-edit-bg)] text-[var(--text-black)] border-[var(--border-item-light)]">#{tag}</span>)}
                      </div>
                    )}
                  </div>
                </details>
              ))}
            </section>
          </main>
        </div>
      </div>

      {/* 🛡️ MASTER ADMIN CMS DASHBOARD MODAL */}
      {showAdminModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="w-full max-w-4xl h-[85vh] flex flex-col rounded-3xl border-2 shadow-2xl overflow-hidden bg-[var(--component-bg-color)] border-[var(--border-item-light)] text-[var(--text-color)]">
            
            {/* Header & Tabs */}
            <div className="p-5 border-b shrink-0 flex justify-between items-center bg-[var(--bg-item)] border-[var(--border-item-light)]">
              <div className="flex flex-wrap items-center gap-4">
                <h3 className="text-xl font-black text-[var(--yellow-text-color)]">🛠️ Admin CMS</h3>
                {adminViewMode === 'list' && (
                  <div className="flex bg-[var(--component-bg-color)] p-1 rounded-xl shadow-inner border border-[var(--border-item-light)]">
                    <button onClick={() => setAdminTab('category')} className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${adminTab === 'category' ? 'bg-[var(--button-edit-bg)] text-[var(--text-black)] shadow' : 'text-[var(--secondary-text-color)] hover:text-[var(--text-color)]'}`}>หมวดหมู่ (Categories)</button>
                    <button onClick={() => setAdminTab('topic')} className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${adminTab === 'topic' ? 'bg-[var(--button-edit-bg)] text-[var(--text-black)] shadow' : 'text-[var(--secondary-text-color)] hover:text-[var(--text-color)]'}`}>หัวข้อ (Topics)</button>
                    <button onClick={() => setAdminTab('faq')} className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${adminTab === 'faq' ? 'bg-[var(--button-edit-bg)] text-[var(--text-black)] shadow' : 'text-[var(--secondary-text-color)] hover:text-[var(--text-color)]'}`}>คำถาม (FAQs)</button>
                  </div>
                )}
              </div>
              <button onClick={() => setShowAdminModal(false)} className="w-8 h-8 rounded-full bg-[var(--hover-item)] hover:opacity-80 font-bold flex items-center justify-center">✕</button>
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-y-auto p-5 custom-scrollbar">
              
              {/* ============ LIST VIEW ============ */}
              {adminViewMode === 'list' && (
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h4 className="font-bold text-sm">รายการ {adminTab.toUpperCase()}</h4>
                    <button onClick={handleOpenCreateForm} className="px-4 py-2 rounded-xl text-xs font-black shadow hover:scale-105 transition bg-[var(--button-create-bg)] text-[var(--button-text-color)]">
                      + สร้างใหม่
                    </button>
                  </div>

                  {isFetchingAdmin ? (
                    <div className="py-10 text-center text-sm opacity-60 animate-pulse">กำลังโหลดข้อมูล...</div>
                  ) : (
                    <div className="space-y-2">
                      {adminDataList.length === 0 && <div className="py-10 text-center opacity-50 text-xs">ไม่มีข้อมูล</div>}
                      {adminDataList.map((item: any) => (
                        <div key={item.id} className="flex items-center justify-between p-4 rounded-2xl border shadow-sm bg-[var(--bg-item)] border-[var(--border-item-light)]">
                          <div className="min-w-0 flex-1">
                            <h5 className="font-bold text-sm truncate text-[var(--text-color)]">
                              {adminTab === 'faq' ? item.question : item.name}
                            </h5>
                            <p className="text-[10px] opacity-60 truncate mt-1 text-[var(--secondary-text-color)]">
                              Slug/ID: {item.slug || item.id} | Status: <span className={item.status === 'published' ? 'text-[var(--green-text-color)]' : 'text-[var(--orange-text-color-light)]'}>{item.status || 'published'}</span>
                            </p>
                          </div>
                          
                          <div className="flex items-center gap-2 ml-4 shrink-0">
                            {adminTab === 'faq' && (
                              <button onClick={() => handleAdminTogglePublish(item.id, item.status || 'published')} className="p-2 rounded-lg bg-[var(--hover-item)] text-[var(--text-color)] hover:opacity-80 text-xs" title="เปิด/ปิดการเผยแพร่">
                                {item.status === 'published' ? '👁️' : '🔒'}
                              </button>
                            )}
                            <button onClick={() => handleOpenEditForm(item)} className="p-2 rounded-lg bg-blue-500/10 text-[var(--blue-text-color-btn)] hover:bg-blue-500/20 text-xs">✏️</button>
                            <button onClick={() => handleAdminDelete(item.id)} className="p-2 rounded-lg bg-red-500/10 text-[var(--red-text-color-btn)] hover:bg-red-500/20 text-xs">🗑️</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* ============ FORM VIEW ============ */}
              {adminViewMode === 'form' && (
                <form onSubmit={handleFormSubmit} className="space-y-4 max-w-2xl mx-auto">
                  <div className="flex items-center gap-3 mb-6">
                    <button type="button" onClick={() => setAdminViewMode('list')} className="w-8 h-8 rounded-full border flex items-center justify-center bg-[var(--hover-item)] border-[var(--border-item-light)] hover:opacity-80">←</button>
                    <h4 className="font-bold text-lg">{modalMode === 'create' ? 'สร้างข้อมูลใหม่' : 'แก้ไขข้อมูล'}</h4>
                  </div>

                  {adminTab === 'category' && (
                    <>
                      <div>
                        <label className="block text-xs font-bold mb-1 text-[var(--secondary-text-color)]">ชื่อ (Name):</label>
                        <input required value={formCategory.name} onChange={e => setFormCategory({...formCategory, name: e.target.value})} className="w-full p-3 rounded-xl border outline-none transition-all bg-[var(--bg-item)] text-[var(--text-color)] border-[var(--border-item-light)] focus:border-[var(--yellow-text-color)] placeholder-[var(--text-input)]" />
                      </div>
                      <div>
                        <label className="block text-xs font-bold mb-1 text-[var(--secondary-text-color)]">Slug (เฉพาะภาษาอังกฤษ):</label>
                        <input required disabled={modalMode === 'edit'} value={formCategory.slug} onChange={e => setFormCategory({...formCategory, slug: e.target.value})} className="w-full p-3 rounded-xl border outline-none disabled:opacity-50 transition-all bg-[var(--bg-item)] text-[var(--text-color)] border-[var(--border-item-light)] focus:border-[var(--yellow-text-color)] placeholder-[var(--text-input)]" />
                      </div>
                      <div>
                        <label className="block text-xs font-bold mb-1 text-[var(--secondary-text-color)]">คำอธิบาย:</label>
                        <textarea value={formCategory.description} onChange={e => setFormCategory({...formCategory, description: e.target.value})} className="w-full p-3 rounded-xl border outline-none transition-all bg-[var(--bg-item)] text-[var(--text-color)] border-[var(--border-item-light)] focus:border-[var(--yellow-text-color)] placeholder-[var(--text-input)]" rows={3}/>
                      </div>
                    </>
                  )}

                  {adminTab === 'topic' && (
                    <>
                      {modalMode === 'create' && (
                        <div>
                          <label className="block text-xs font-bold mb-1 text-[var(--yellow-text-color)]">หมวดหมู่:</label>
                          <select required value={formTopic.categoryId} onChange={e => setFormTopic({...formTopic, categoryId: e.target.value})} className="w-full p-3 rounded-xl border outline-none font-bold transition-all bg-[var(--bg-item)] text-[var(--text-color)] border-[var(--border-item-light)] focus:border-[var(--yellow-text-color)]">
                            <option value="" disabled className="text-[var(--secondary-text-color)]">-- เลือกหมวดหมู่ --</option>
                            {categories.map(c => <option key={c.id} value={c.id} className="bg-[var(--bg-item)] text-[var(--text-color)]">{c.name}</option>)}
                          </select>
                        </div>
                      )}
                      <div>
                        <label className="block text-xs font-bold mb-1 text-[var(--secondary-text-color)]">ชื่อ (Name):</label>
                        <input required value={formTopic.name} onChange={e => setFormTopic({...formTopic, name: e.target.value})} className="w-full p-3 rounded-xl border outline-none transition-all bg-[var(--bg-item)] text-[var(--text-color)] border-[var(--border-item-light)] focus:border-[var(--yellow-text-color)] placeholder-[var(--text-input)]" />
                      </div>
                      {modalMode === 'create' && (
                        <div>
                          <label className="block text-xs font-bold mb-1 text-[var(--secondary-text-color)]">Slug:</label>
                          <input required value={formTopic.slug} onChange={e => setFormTopic({...formTopic, slug: e.target.value})} className="w-full p-3 rounded-xl border outline-none transition-all bg-[var(--bg-item)] text-[var(--text-color)] border-[var(--border-item-light)] focus:border-[var(--yellow-text-color)] placeholder-[var(--text-input)]" />
                        </div>
                      )}
                      <div>
                        <label className="block text-xs font-bold mb-1 text-[var(--secondary-text-color)]">รายละเอียด:</label>
                        <textarea value={formTopic.descriptionHtml} onChange={e => setFormTopic({...formTopic, descriptionHtml: e.target.value})} className="w-full p-3 rounded-xl border outline-none transition-all bg-[var(--bg-item)] text-[var(--text-color)] border-[var(--border-item-light)] focus:border-[var(--yellow-text-color)] placeholder-[var(--text-input)]" rows={4}/>
                      </div>
                      <div>
                        <label className="block text-xs font-bold mb-1 text-[var(--secondary-text-color)]">Tags (คั่นด้วย ,):</label>
                        <input value={formTopic.tags} onChange={e => setFormTopic({...formTopic, tags: e.target.value})} className="w-full p-3 rounded-xl border outline-none transition-all bg-[var(--bg-item)] text-[var(--text-color)] border-[var(--border-item-light)] focus:border-[var(--yellow-text-color)] placeholder-[var(--text-input)]" />
                      </div>
                    </>
                  )}

                  {adminTab === 'faq' && (
                    <>
                      {modalMode === 'create' && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-bold mb-1 text-[var(--yellow-text-color)]">หมวดหมู่:</label>
                            <select required value={formFaq.categoryId} onChange={e => setFormFaq({...formFaq, categoryId: e.target.value, topicId: ''})} className="w-full p-3 rounded-xl border outline-none font-bold transition-all bg-[var(--bg-item)] text-[var(--text-color)] border-[var(--border-item-light)] focus:border-[var(--yellow-text-color)]">
                              <option value="" disabled className="text-[var(--secondary-text-color)]">-- เลือกหมวดหมู่ --</option>
                              {categories.map(c => <option key={c.id} value={c.id} className="bg-[var(--bg-item)] text-[var(--text-color)]">{c.name}</option>)}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-bold mb-1 text-[var(--yellow-text-color)]">หัวข้อ:</label>
                            <select required disabled={!formFaq.categoryId} value={formFaq.topicId} onChange={e => setFormFaq({...formFaq, topicId: e.target.value})} className="w-full p-3 rounded-xl border outline-none font-bold disabled:opacity-50 transition-all bg-[var(--bg-item)] text-[var(--text-color)] border-[var(--border-item-light)] focus:border-[var(--yellow-text-color)]">
                              <option value="" disabled className="text-[var(--secondary-text-color)]">-- เลือกหัวข้อ --</option>
                              {categories.find(c => c.id === formFaq.categoryId)?.topics?.map(t => <option key={t.id} value={t.id} className="bg-[var(--bg-item)] text-[var(--text-color)]">{t.name}</option>)}
                            </select>
                          </div>
                        </div>
                      )}
                      <div>
                        <label className="block text-xs font-bold mb-1 text-[var(--secondary-text-color)]">คำถาม:</label>
                        <input required value={formFaq.question} onChange={e => setFormFaq({...formFaq, question: e.target.value})} className="w-full p-3 rounded-xl border outline-none transition-all bg-[var(--bg-item)] text-[var(--text-color)] border-[var(--border-item-light)] focus:border-[var(--yellow-text-color)] placeholder-[var(--text-input)]" />
                      </div>
                      <div>
                        <label className="block text-xs font-bold mb-1 text-[var(--secondary-text-color)]">คำตอบ:</label>
                        <textarea required value={formFaq.answerHtml} onChange={e => setFormFaq({...formFaq, answerHtml: e.target.value})} className="w-full p-3 rounded-xl border outline-none transition-all bg-[var(--bg-item)] text-[var(--text-color)] border-[var(--border-item-light)] focus:border-[var(--yellow-text-color)] placeholder-[var(--text-input)]" rows={5}/>
                      </div>
                      <div>
                        <label className="block text-xs font-bold mb-1 text-[var(--secondary-text-color)]">Tags (คั่นด้วย ,):</label>
                        <input value={formFaq.tags} onChange={e => setFormFaq({...formFaq, tags: e.target.value})} className="w-full p-3 rounded-xl border outline-none transition-all bg-[var(--bg-item)] text-[var(--text-color)] border-[var(--border-item-light)] focus:border-[var(--yellow-text-color)] placeholder-[var(--text-input)]" />
                      </div>
                      <div>
                        <label className="block text-xs font-bold mb-1 text-[var(--secondary-text-color)]">Keywords (คั่นด้วย ,):</label>
                        <input value={formFaq.keywords} onChange={e => setFormFaq({...formFaq, keywords: e.target.value})} className="w-full p-3 rounded-xl border outline-none transition-all bg-[var(--bg-item)] text-[var(--text-color)] border-[var(--border-item-light)] focus:border-[var(--yellow-text-color)] placeholder-[var(--text-input)]" />
                      </div>
                    </>
                  )}

                  <div className="flex justify-end pt-4 border-t border-[var(--border-item-light)]">
                    <button type="submit" className="px-8 py-3 rounded-xl font-black shadow-lg hover:scale-105 transition bg-[var(--button-create-bg)] text-[var(--button-text-color)]">
                      บันทึกข้อมูล
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}